import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { MapPin, Briefcase, Building2, ArrowRight } from "lucide-react";

const Careers = () => {
  const { lang, t } = useLang();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/jobs").then((r) => {
      setJobs(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="careers-page">
      {/* HERO */}
      <section className="relative grain-bg min-h-[55vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1450778869180-41d0601e046e?auto=format&fit=crop&w=1600&q=80"
            alt="Careers"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FAF9F6] via-[#FAF9F6]/85 to-transparent" />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 w-full py-20 fade-up">
          <p className="overline mb-3">{t("careers.heroOverline")}</p>
          <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6 max-w-2xl">
            {t("careers.heroTitle")}
          </h1>
          <p className="text-base lg:text-lg text-[#736B66] max-w-xl leading-relaxed">
            {t("careers.heroSubtitle")}
          </p>
        </div>
      </section>

      {/* JOB LISTINGS */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-20">
        <h2 className="font-serif text-3xl lg:text-4xl mb-10">{t("careers.openPositions")}</h2>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-[#F5F2ED] animate-pulse" />
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <p className="text-[#736B66] text-center py-20">{t("careers.noJobs")}</p>
        ) : (
          <div className="space-y-4">
            {jobs.map((job, idx) => {
              const title = lang === "el" ? job.title_el : job.title_en;
              return (
                <Link
                  key={job.id}
                  to={`/careers/${job.id}`}
                  className="block group bg-white border border-[#EBE5DF] hover:border-[#D4AF37] p-6 lg:p-8 transition-all fade-up"
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  data-testid={`job-${job.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-serif text-2xl lg:text-3xl mb-3 group-hover:text-[#D4AF37] transition-colors">
                        {title}
                      </h3>
                      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#736B66]">
                        <span className="flex items-center gap-2">
                          <MapPin size={14} className="text-[#D4AF37]" /> {job.location}
                        </span>
                        <span className="flex items-center gap-2">
                          <Briefcase size={14} className="text-[#D4AF37]" />
                          {t(`careers.employment.${job.employment_type}`)}
                        </span>
                        {job.department && (
                          <span className="flex items-center gap-2">
                            <Building2 size={14} className="text-[#D4AF37]" /> {job.department}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="luxury-btn-outline whitespace-nowrap">
                      {t("careers.viewDetails")} <ArrowRight size={14} className="hero-arrow" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Careers;
